import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useCart } from '../lib/cart';
import { useStore } from '../lib/store';
import { usePageMeta } from '../lib/meta';
import { countryName, deliveryEstimate, price, priceOrFree } from '../lib/format';
import { Alert, Button, EmptyState, Spinner } from '../components/ui';

interface AddressForm {
  name: string;
  line1: string;
  line2: string;
  city: string;
  county: string;
  postcode: string;
  countryCode: string;
  phone: string;
}

const EMPTY_ADDRESS: AddressForm = {
  name: '',
  line1: '',
  line2: '',
  city: '',
  county: '',
  postcode: '',
  countryCode: 'GB',
  phone: '',
};

function Field({
  label,
  value,
  onChange,
  required = false,
  type = 'text',
  autoComplete,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  autoComplete?: string;
  error?: string;
}) {
  // Digits must survive here: stripping them collapses "Address line 1" and
  // "Address line 2" onto the same id, which silently points the second
  // label at the first input.
  const id = `field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-xs uppercase tracking-widest text-muted">
        {label}
        {/* aria-hidden so the asterisk stays a visual cue only. Without it the
            accessible name becomes "Address line 1*", which screen readers
            announce as "star" — the `required` attribute already conveys it. */}
        {required && (
          <span aria-hidden="true" className="ml-1 text-error">
            *
          </span>
        )}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        required={required}
        autoComplete={autoComplete}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={error ? true : undefined}
        className={`w-full border bg-transparent px-3 py-2.5 text-sm outline-none transition-colors ${
          error ? 'border-error' : 'border-border focus:border-ink'
        }`}
      />
      {error && <p className="mt-1 text-xs text-error">{error}</p>}
    </div>
  );
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, update, refresh, reset, loading: cartLoading } = useCart();
  const { principal } = useAuth();
  const { data: storeData } = useStore();

  const [email, setEmail] = useState('');
  const [address, setAddress] = useState<AddressForm>(EMPTY_ADDRESS);
  const [notes, setNotes] = useState('');
  const [countries, setCountries] = useState<string[]>([]);
  const [shipsWorldwide, setShipsWorldwide] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  usePageMeta({ title: `Checkout | ${storeData?.store?.name ?? "Gill's Handmade"}` });

  // Prefill from the signed-in account, but never overwrite something already typed.
  useEffect(() => {
    if (principal?.email && !email) setEmail(principal.email);
    if (principal?.name) {
      setAddress((prev) => (prev.name ? prev : { ...prev, name: principal.name }));
    }
  }, [principal, email]);

  useEffect(() => {
    if (cart?.email && !email) setEmail(cart.email);
  }, [cart, email]);

  useEffect(() => {
    void api
      .shippingCountries()
      .then((response) => {
        setCountries(response.countries);
        setShipsWorldwide(response.shipsWorldwide);
      })
      .catch(() => setCountries(['GB']));
  }, []);

  // Re-quote shipping whenever the destination changes.
  useEffect(() => {
    if (!cart || cart.shippingCountry === address.countryCode) return;
    void update({ shippingCountry: address.countryCode });
  }, [address.countryCode, cart, update]);

  const totals = cart?.totals;
  const currency = totals?.currency ?? 'GBP';
  const quotes = totals?.shippingQuotes ?? [];

  const shippableCountries = useMemo(() => {
    const list = [...countries];
    if (shipsWorldwide) {
      // A catch-all zone means anywhere is allowed; offer the full ISO list so
      // the picker is not artificially limited to named zones.
      for (const code of ['US', 'CA', 'AU', 'NZ', 'JP', 'SG', 'ZA', 'AE']) {
        if (!list.includes(code)) list.push(code);
      }
    }
    return list.sort((a, b) => countryName(a).localeCompare(countryName(b)));
  }, [countries, shipsWorldwide]);

  if (!cart || !totals || totals.lines.length === 0) {
    return (
      <EmptyState
        title="Your basket is empty"
        message="Add a piece or two and come back to complete your order."
        action={
          <Link to="/collections/all-jewellery" className="text-sm underline underline-offset-4">
            Browse jewellery
          </Link>
        }
      />
    );
  }

  const oversubscribed = totals.lines.filter((line) => line.exceedsStock);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const { order } = await api.checkout({
        cartId: cart!.id,
        email,
        shippingAddress: address,
        billingSameAsShipping: true,
        notes,
      });

      // The test provider confirms immediately. With Stripe this is where
      // stripe.js would confirm the PaymentIntent using the returned
      // clientSecret, and the webhook would mark the order paid.
      const confirmed = await api.confirmOrder(order.id);

      await reset();
      navigate(`/orders/${confirmed.order.id}?email=${encodeURIComponent(email)}`, {
        replace: true,
      });
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
        // A conflict means stock moved under us — re-price so the basket shows
        // the new reality rather than stale availability.
        if (err.status === 409) await refresh();
      } else {
        setError('We could not complete your order. Please try again.');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page-container py-10">
      <h1 className="mb-8 text-3xl">Checkout</h1>

      {error && (
        <div className="mb-6">
          <Alert onDismiss={() => setError(null)}>{error}</Alert>
        </div>
      )}

      {oversubscribed.length > 0 && (
        <div className="mb-6">
          <Alert>
            Some pieces in your basket are no longer available in that quantity. Please
            adjust your basket before continuing.
          </Alert>
        </div>
      )}

      <div className="grid gap-12 lg:grid-cols-[1fr_400px]">
        <form onSubmit={submit} className="space-y-10">
          <section>
            <h2 className="mb-4 border-b border-border pb-2 text-lg">Contact</h2>
            <p className="mb-4 text-xs text-muted">
              No account needed. We will email your order confirmation, and you
              can find the order again any time with its number and this address.
            </p>
            <Field
              label="Email address"
              type="email"
              value={email}
              onChange={setEmail}
              required
              autoComplete="email"
            />
            <p className="mt-2 text-xs text-muted">
              We will send your order confirmation here.
            </p>
          </section>

          <section>
            <h2 className="mb-4 border-b border-border pb-2 text-lg">Delivery address</h2>
            <div className="grid gap-4">
              <Field
                label="Full name"
                value={address.name}
                onChange={(v) => setAddress({ ...address, name: v })}
                required
                autoComplete="name"
              />
              <Field
                label="Address line 1"
                value={address.line1}
                onChange={(v) => setAddress({ ...address, line1: v })}
                required
                autoComplete="address-line1"
              />
              <Field
                label="Address line 2"
                value={address.line2}
                onChange={(v) => setAddress({ ...address, line2: v })}
                autoComplete="address-line2"
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Town or city"
                  value={address.city}
                  onChange={(v) => setAddress({ ...address, city: v })}
                  required
                  autoComplete="address-level2"
                />
                <Field
                  label="County"
                  value={address.county}
                  onChange={(v) => setAddress({ ...address, county: v })}
                  autoComplete="address-level1"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Postcode"
                  value={address.postcode}
                  onChange={(v) => setAddress({ ...address, postcode: v })}
                  required
                  autoComplete="postal-code"
                />
                <div>
                  <label
                    htmlFor="country"
                    className="mb-1.5 block text-xs uppercase tracking-widest text-muted"
                  >
                    Country<span className="ml-1 text-error">*</span>
                  </label>
                  <select
                    id="country"
                    value={address.countryCode}
                    onChange={(event) =>
                      setAddress({ ...address, countryCode: event.target.value })
                    }
                    autoComplete="country"
                    className="w-full cursor-pointer border border-border bg-transparent px-3 py-2.5 text-sm outline-none focus:border-ink"
                  >
                    {shippableCountries.map((code) => (
                      <option key={code} value={code}>
                        {countryName(code)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <Field
                label="Phone (optional)"
                type="tel"
                value={address.phone}
                onChange={(v) => setAddress({ ...address, phone: v })}
                autoComplete="tel"
              />
            </div>
          </section>

          <section>
            <h2 className="mb-4 border-b border-border pb-2 text-lg">Delivery method</h2>
            {cartLoading ? (
              <Spinner />
            ) : quotes.length === 0 ? (
              <Alert tone="info">
                We do not currently ship to {countryName(address.countryCode)}. Please get
                in touch and we will see what we can arrange.
              </Alert>
            ) : (
              <div className="space-y-2">
                {quotes.map((quote) => (
                  <label
                    key={quote.rateId}
                    className={`flex cursor-pointer items-center gap-3 border p-4 transition-colors ${
                      totals.selectedShippingRateId === quote.rateId
                        ? 'border-ink'
                        : 'border-border hover:border-muted'
                    }`}
                  >
                    <input
                      type="radio"
                      name="shipping"
                      checked={totals.selectedShippingRateId === quote.rateId}
                      onChange={() => void update({ selectedShippingRateId: quote.rateId })}
                      className="accent-[var(--color-primary)]"
                    />
                    <span className="flex-1">
                      <span className="block text-sm">{quote.name}</span>
                      <span className="block text-xs text-muted">
                        {deliveryEstimate(quote.minDeliveryDays, quote.maxDeliveryDays)}
                        {quote.description ? ` · ${quote.description}` : ''}
                      </span>
                    </span>
                    <span className="text-sm">
                      {quote.freeByThreshold && quote.basePriceMinor > 0 && (
                        <span className="mr-2 text-muted line-through">
                          {price(quote.basePriceMinor, currency)}
                        </span>
                      )}
                      {priceOrFree(quote.priceMinor, currency)}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-4 border-b border-border pb-2 text-lg">Order notes</h2>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
              placeholder="Gift message, delivery instructions…"
              aria-label="Order notes"
              className="w-full border border-border bg-transparent px-3 py-2.5 text-sm outline-none focus:border-ink"
            />
          </section>

          <section>
            <h2 className="mb-4 border-b border-border pb-2 text-lg">Payment</h2>
            <div className="border border-border bg-surface p-5">
              <p className="text-sm">Test checkout</p>
              <p className="mt-2 text-xs leading-relaxed text-muted">
                No card is taken and no money moves. Orders are recorded exactly as they
                will be with a real payment provider, so the whole flow can be tried
                end to end. Connect Stripe to take live payments.
              </p>
              <p className="mt-3 text-xs text-muted">
                Any order total ending in .13 is declined on purpose, so the failure path
                can be tested too.
              </p>
            </div>
          </section>

          <Button
            type="submit"
            size="lg"
            className="w-full"
            loading={submitting}
            disabled={submitting || quotes.length === 0 || oversubscribed.length > 0}
          >
            Place order · {price(totals.totalMinor, currency)}
          </Button>
        </form>

        {/* Order summary */}
        <aside className="lg:sticky lg:top-32 lg:self-start">
          <div className="border border-border p-6">
            <h2 className="mb-5 text-lg">Your order</h2>

            <ul className="mb-5 divide-y divide-border">
              {totals.lines.map((line) => (
                <li key={line.productId} className="flex gap-3 py-3">
                  <div className="relative h-16 w-14 shrink-0 overflow-hidden bg-surface">
                    {line.image && (
                      <img
                        src={line.image.src}
                        alt={line.image.alt}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    )}
                    <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] text-primary-ink">
                      {line.quantity}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col justify-center">
                    <p className="text-xs leading-snug">
                      {line.title.split('|')[0]?.trim() ?? line.title}
                    </p>
                    {line.discounts.length > 0 && (
                      <p className="mt-0.5 text-[10px] uppercase tracking-wider text-sale">
                        {line.discounts.map((d) => d.title).join(' · ')}
                      </p>
                    )}
                  </div>
                  <p className="text-xs">{price(line.lineTotalMinor, currency)}</p>
                </li>
              ))}
            </ul>

            <dl className="space-y-2 border-t border-border pt-4 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted">Subtotal</dt>
                <dd>{price(totals.subtotalMinor, currency)}</dd>
              </div>
              {totals.discountMinor > 0 && (
                <div className="flex justify-between text-sale">
                  <dt>
                    Discounts
                    {totals.appliedDiscounts.length > 0 && (
                      <span className="block text-[10px] uppercase tracking-wider opacity-80">
                        {totals.appliedDiscounts.map((d) => d.title).join(', ')}
                      </span>
                    )}
                  </dt>
                  <dd>−{price(totals.discountMinor, currency)}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-muted">Delivery</dt>
                <dd>
                  {totals.shippingDiscountMinor > 0
                    ? 'Free'
                    : priceOrFree(totals.shippingMinor, currency)}
                </dd>
              </div>
              <div className="flex justify-between border-t border-border pt-3 text-lg">
                <dt>Total</dt>
                <dd className="font-bold">{price(totals.totalMinor, currency)}</dd>
              </div>
            </dl>
          </div>
        </aside>
      </div>
    </div>
  );
}
